import { styled } from '@mui/material';
import React, { useState } from 'react';
import { ComboSelect, Typography } from '../../components/atoms';
import { UniversalCard } from '../../components/molecules/UniversalCard/UniversalCard';
import { useAppDispatch, useAppSelector } from '../../common/hooks';
import { ReactComponent as RiskToleranceIcon } from '../../assets/icons/risktoleranceicon.svg';
import { saveClient } from '../../features/client';
import { saveGoal } from '../../features/client-goals';
import { isStageDisabled } from '../../common/helper';
import { CLIENT_PROFILE_STAGE_KEYS } from '../../common/constants';
import { useTranslation } from 'react-i18next';
import { HeaderIcon } from './HeaderIcon';
import { InfoTooltip } from '../../components/molecules/InfoTooltip';
import { getRiskProfileMessage, RiskProfileType } from '../../utils/risk-profile';

export interface RiskToleranceCardProps {}

const ContentDiv = styled('div')(({ theme }) => ({
    maxWidth: '50%',
    margin: '0 auto',
    marginBlock: theme.spacing(7)
}));

export const RiskToleranceCard: React.FC<RiskToleranceCardProps> = ({}) => {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const { orgInfo } = useAppSelector((state) => state.global);
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const adviceGoals = useAppSelector((state) => state?.adviceGoals);
    // getting options from organization
    const options = orgInfo?.riskProfileAex[i18n.language] || orgInfo?.riskProfileAex['en'] || [];
    const defaultSelectedOption =
        adviceClient?.riskProfile !== '' ? adviceClient?.riskProfile : options?.length > 1 ? options?.[0]?.value : null;
    const [selectedOption, setSelectedOption] = useState<RiskProfileType | null>(defaultSelectedOption);

    const syncRiskSelected = async (selectedOption: string | null) => {
        if (selectedOption && options?.find((opt: any) => opt.key === selectedOption)) {
            // API call to persist riskProfile and stages
            const updatedAdviceClient = {
                ...adviceClient,
                riskProfile: selectedOption,
                stages: [...adviceClient?.stages, CLIENT_PROFILE_STAGE_KEYS[2], CLIENT_PROFILE_STAGE_KEYS[3]]
            };
            await dispatch(saveClient(updatedAdviceClient));

            let goalsData: typeof adviceGoals = JSON.parse(JSON.stringify(adviceGoals));

            if (goalsData?.response.length > 0) {
                goalsData.response = [];
                goalsData.global = {
                    ...goalsData.global,
                    status: 'incomplete',
                    enablePDFGenerate: false,
                    enablePlanDetails: false
                };
                await dispatch(saveGoal(goalsData));
            }
        }
    };

    const handleSelectionChange = (value: RiskProfileType) => {
        setSelectedOption(value);
        syncRiskSelected(value);
    };

    const formattedOptions = options?.map((opt: any) => ({ value: opt?.key, label: opt?.label }));

    const riskProfileMessage = getRiskProfileMessage(selectedOption!, t);

    return (
        <UniversalCard
            header={t('CLIENT_PROFILE_RISK_TOLERANCE_HEADING')}
            headerIcon={
                <HeaderIcon
                    isDisabled={isStageDisabled(adviceClient, CLIENT_PROFILE_STAGE_KEYS[2])}
                    icon={<RiskToleranceIcon />}
                />
            }
            cardSx={
                isStageDisabled(adviceClient, CLIENT_PROFILE_STAGE_KEYS[2])
                    ? {
                          cursor: 'not-allowed',
                          '*': { 'pointer-events': 'none', filter: 'grayscale(100%)' }
                      }
                    : {}
            }
        >
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                    variant="body.large"
                    t={
                        selectedOption === options?.[0]?.value
                            ? 'CLIENT_PROFILE_RISK_TOLERANCE_SUBHEADING'
                            : riskProfileMessage
                    }
                />
                <InfoTooltip width={381} placement="left-start" t="RISK_TOLERANCE_HELPER"></InfoTooltip>
            </div>
            <ContentDiv>
                <Typography label={t('CLIENT_PROFILE_RISK_TOLERANCE_HELPER')} marginBottom={0} />
                <ComboSelect
                    options={formattedOptions}
                    selectedValue={selectedOption || ''}
                    onSelectChange={handleSelectionChange}
                />
            </ContentDiv>
        </UniversalCard>
    );
};
